import { useMemo } from 'preact/hooks';

import { isMessageJsonButtons } from '../types/guards';
import type { MessageWithButtons } from '../types/messages';
import { WebsocketMessageContentType } from '../types/ws-events';
import { sanitizeHtml } from '../utils';

/**
 * Хук для определения контента сообщения с кнопками
 * Использует titleHtml/title из messageJson, если они есть, иначе fallback на message/messageHtml
 * Учитывает messageContentType для определения, использовать ли HTML или текст
 */
export const useMessageContent = (messageProps: MessageWithButtons) => {
  const { messageJson, message, messageHtml, messageContentType } = messageProps;

  return useMemo(() => {
    const messageJsonData = isMessageJsonButtons(messageJson) ? messageJson : null;
    const titleHtml = messageJsonData?.titleHtml;
    const title = messageJsonData?.title;

    // Для сообщений с кнопками приоритет у titleHtml/title из messageJson
    const hasTitleHtml = !!titleHtml;
    const sanitizedTitleHtml = hasTitleHtml ? sanitizeHtml(titleHtml) : null;
    const hasValidTitleHtml = hasTitleHtml && sanitizedTitleHtml;

    // Если есть titleHtml - используем его, иначе проверяем messageContentType
    if (hasValidTitleHtml) {
      return {
        message: '',
        messageHtml: sanitizedTitleHtml,
      };
    }

    // Если нет titleHtml, проверяем messageContentType для message/messageHtml
    const isHtmlContent = messageContentType === WebsocketMessageContentType.Html;
    const displayMessageHtml = isHtmlContent && messageHtml ? sanitizeHtml(messageHtml) : undefined;
    const displayMessage = isHtmlContent ? '' : title || message;

    return {
      message: displayMessage,
      messageHtml: displayMessageHtml,
    };
  }, [messageJson, message, messageHtml, messageContentType]);
};
